// Fase 5 · Grabador de eventos de cliente para el panel de depuración en vivo.
//
// Solo graba con el monitor ENCENDIDO (sesión de depuración activada desde el
// admin → broadcast `monitorToggle`, flag `APIMONITOR_ENABLED=1`). Apagado,
// `record()` sale en la primera línea: sin asignaciones ni Redux.
//
// Buffer fuera de Redux (no provoca renders) + copia en localStorage para
// sobrevivir a recargas. `useApiMonitorPersist` lo vacía cada 5 s por el
// `POST api-monitor/batch` existente: `peek(n)` → POST → `commit(n)` solo tras
// 200 OK (un 429 deja los items en la cola para reintentarlos).
//
// Tipos: "ui" (click), "hub_in" (evento recibido por SignalR), "conn"
// (reconexiones / cierres del hub), "lifecycle" (carga, visibilidad,
// online/offline, navegación), "dup" (duplicados y conflictos bloqueados).

import { getCurrentAction, getScreen } from "./debugTrace";

export const MAX_QUEUE = 2000;
export const DETAIL_MAX = 1000;
const MONITOR_KEY = "APIMONITOR_ENABLED";

let queue = [];
let storageKey = null;
let persistTimer = null;
let monitoringProbe = defaultProbe;
let probeCache = { at: 0, value: false };

function defaultProbe() {
  try {
    return window.localStorage.getItem(MONITOR_KEY) === "1";
  } catch {
    return false;
  }
}

/** El flag se lee como mucho una vez por segundo (clicks muy seguidos). */
export function isRecording(now = Date.now()) {
  if (now - probeCache.at < 1000) return probeCache.value;
  probeCache = { at: now, value: !!monitoringProbe() };
  return probeCache.value;
}

/** Forzar la relectura del flag (al encender/apagar el monitor). */
export function refreshRecordingFlag() {
  probeCache = { at: 0, value: false };
}

export function __setMonitoringProbeForTests(fn) {
  monitoringProbe = fn || defaultProbe;
  refreshRecordingFlag();
}

export function __resetForTests() {
  queue = [];
  storageKey = null;
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = null;
  monitoringProbe = defaultProbe;
  refreshRecordingFlag();
}

function trimDetail(detail) {
  if (detail == null) return null;
  let s = typeof detail === "string" ? detail : safeJson(detail);
  if (s == null) return null;
  return s.length <= DETAIL_MAX ? s : s.slice(0, DETAIL_MAX);
}

function safeJson(v) {
  try { return JSON.stringify(v); } catch { return null; }
}

// ── Persistencia local (sobrevive a recargas) ─────────────────────────

export function attachStorage(deviceId) {
  storageKey = `tmo-debug-queue-${deviceId || "anon"}`;
  try {
    const saved = JSON.parse(window.localStorage.getItem(storageKey) || "[]");
    if (Array.isArray(saved) && saved.length) {
      // Lo guardado va delante: es más antiguo que lo grabado en esta carga.
      queue = saved.concat(queue).slice(-MAX_QUEUE);
    }
  } catch { /* cola corrupta → se descarta */ }
}

function schedulePersist() {
  if (!storageKey || persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    persistNow();
  }, 1000);
}

export function persistNow() {
  if (!storageKey) return;
  try {
    if (queue.length) window.localStorage.setItem(storageKey, JSON.stringify(queue));
    else window.localStorage.removeItem(storageKey);
  } catch { /* cuota llena → solo memoria */ }
}

// ── API ───────────────────────────────────────────────────────────────

/**
 * Graba un evento. `fields`: actionId/actionName (por defecto, la acción en
 * curso), screen (por defecto la actual), x, y, viewportW, viewportH,
 * target, eventType, seq, detail, durationMs, isError.
 */
export function record(kind, fields = {}) {
  if (!isRecording()) return false;
  const action = fields.actionId === undefined ? getCurrentAction() : null;
  queue.push({
    kind,
    ts: Date.now(),
    actionId: fields.actionId !== undefined ? fields.actionId : action?.id || null,
    actionName: fields.actionName !== undefined ? fields.actionName : action?.name || null,
    screen: fields.screen || getScreen(),
    x: fields.x ?? null,
    y: fields.y ?? null,
    viewportW: fields.viewportW ?? null,
    viewportH: fields.viewportH ?? null,
    target: fields.target ? String(fields.target).slice(0, 200) : null,
    eventType: fields.eventType ? String(fields.eventType).slice(0, 80) : null,
    seq: fields.seq ?? null,
    durationMs: fields.durationMs ?? null,
    isError: !!fields.isError,
    detail: trimDetail(fields.detail),
  });
  if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);
  schedulePersist();
  return true;
}

export function size() {
  return queue.length;
}

/** Los `n` primeros eventos sin quitarlos de la cola. */
export function peek(n = 200) {
  return queue.slice(0, Math.max(0, n));
}

/** Quita los `n` primeros (tras un POST correcto). */
export function commit(n) {
  if (!n) return;
  queue.splice(0, n);
  schedulePersist();
}

export function clear() {
  queue = [];
  persistNow();
}

// ── Mapeo al item del batch `/api-monitor/batch` ──────────────────────

const METHOD_BY_KIND = {
  ui: "UI",
  hub_in: "HUB_IN",
  conn: "CONN",
  lifecycle: "LIFECYCLE",
  dup: "DUP",
};

/**
 * Item del batch para un evento del recorder. `method`/`url` siguen el patrón
 * de `CLIENT_ERROR` (el panel antiguo los enseña legibles aunque no conozca
 * los campos nuevos).
 */
export function toBatchItem(ev, { deviceId, deviceLabel, source }) {
  const url =
    ev.kind === "ui" ? ev.target || ev.actionName
      : ev.kind === "hub_in" ? ev.eventType
        : ev.eventType || ev.target || ev.kind;
  return {
    deviceId,
    deviceLabel,
    source,
    kind: ev.kind,
    method: METHOD_BY_KIND[ev.kind] || String(ev.kind || "").toUpperCase(),
    url: url ? String(url).slice(0, 500) : null,
    status: null,
    durationMs: ev.durationMs != null ? Math.round(ev.durationMs) : 0,
    requestBody: null,
    responseBody: null,
    errorMessage: null,
    traceId: null,
    isError: !!ev.isError,
    startedAt: new Date(ev.ts).toISOString(),
    actionId: ev.actionId,
    actionName: ev.actionName,
    screen: ev.screen,
    x: ev.x,
    y: ev.y,
    viewportW: ev.viewportW,
    viewportH: ev.viewportH,
    target: ev.target,
    eventType: ev.eventType,
    seq: ev.seq,
    detail: ev.detail,
  };
}
